import { Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useEffect, useRef } from 'react';
import MainLayout from '@components/layout/MainLayout';
import Dashboard from '@pages/Dashboard';
import People from '@pages/People';
import Organisations from '@pages/Organisations';
import Volunteers from '@pages/Volunteers';
import Assets from '@pages/Assets';
import Reports from '@pages/Reports';
import Settings from '@pages/Settings';
import TeamManagement from '@pages/TeamManagement';
import FreeAgent from '@pages/FreeAgent';
import SystemSettings from '@pages/SystemSettings';
import SystemStatus from '@pages/SystemStatus';
import Login from '@pages/Login';
import AccessDenied from '@pages/AccessDenied';
import AccountDeactivated from '@pages/AccountDeactivated';
import { useIdleTimeout } from '@hooks/useIdleTimeout';
import { useTeamMembership } from '@hooks/useTeam';
import { useAuthStore } from '@store/authStore';
import { SessionTimeoutModal } from '@components/common/SessionTimeoutModal';
import { ModuleGate } from '@components/common/ModuleGate';

/**
 * Loading spinner for team membership check
 */
function TeamCheckLoading() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sca-purple-500 to-sca-pink-500 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-sca-purple-500 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-600">Checking access...</p>
      </div>
    </div>
  );
}

/**
 * Protected route that also checks team membership
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const { checkMembership } = useTeamMembership();
  const { teamStatus, setTeamMembership, setUser, showSessionTimeout, setShowSessionTimeout } = useAuthStore();

  // Prevent re-checking after a completed attempt (success or failure)
  const hasChecked = useRef(false);

  // Enable idle timeout for authenticated users
  const { resetTimer } = useIdleTimeout();

  // Handle session timeout modal continue
  const handleSessionContinue = () => {
    setShowSessionTimeout(false);
    resetTimer();
  };

  // Check team membership once when authenticated.
  // We always re-check on mount (ignoring persisted state) so that a stale
  // cached role (e.g. from a previous API failure that fell back to 'Regular')
  // can never permanently hide Admin features after a page reload.
  // If we already have a persisted 'member' state we show content immediately
  // and refresh the role silently in the background to avoid a loading flash.
  useEffect(() => {
    async function checkTeam() {
      if (!isAuthenticated || accounts.length === 0) return;
      if (hasChecked.current) return; // guard against double-fire

      hasChecked.current = true;

      // If we have no prior membership result, show the loading state
      if (teamStatus === 'unknown') {
        setTeamMembership('checking');
      }
      // If teamStatus is already 'member' we proceed silently (no loading flash)

      const result = await checkMembership();

      if (!result) {
        // API error — read current teamStatus from the store at this moment
        // (not from the stale closure value captured at effect-setup time) so
        // we don't accidentally downgrade a cached Admin role to Regular.
        const currentStatus = useAuthStore.getState().teamStatus;
        const currentRole = useAuthStore.getState().teamRole;
        console.warn('Failed to check team membership, allowing access. currentStatus=' + currentStatus + ' currentRole=' + currentRole);
        if (currentStatus !== 'member') {
          setTeamMembership('member', 'Regular', null, null);
        }
        // If already 'member', keep whatever role is persisted — don't downgrade.
        return;
      }

      if (result.isMember && result.member) {
        setTeamMembership('member', result.member.role, result.member.id, result.member.modulePermissions ?? null, result.member.appAccess ?? null);
        setUser({
          id: result.member.id,
          name: result.member.name,
          email: result.member.email,
          role: result.member.role === 'Admin' ? 'admin' : result.member.role === 'ReadOnly' ? 'readonly' : 'staff',
        });
      } else if (result.reason === 'deactivated') {
        setTeamMembership('deactivated');
      } else {
        setTeamMembership('not_in_team');
      }
    }

    checkTeam();
  // teamStatus intentionally omitted from deps — we read it once at call time
  // but don't want the effect to re-fire when it changes.
  }, [isAuthenticated, accounts, checkMembership, setTeamMembership, setUser]);

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Still checking team membership
  if (teamStatus === 'unknown' || teamStatus === 'checking') {
    return <TeamCheckLoading />;
  }

  // Not in team - show access denied
  if (teamStatus === 'not_in_team') {
    return <AccessDenied />;
  }

  // Deactivated - show deactivated page
  if (teamStatus === 'deactivated') {
    return <AccountDeactivated />;
  }

  return (
    <>
      {children}
      <SessionTimeoutModal
        isOpen={showSessionTimeout}
        onContinue={handleSessionContinue}
      />
    </>
  );
}

/**
 * Admin-only route wrapper
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { teamRole } = useAuthStore();

  if (teamRole !== 'Admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/account-deactivated" element={<AccountDeactivated />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/people" element={<ModuleGate module="people"><People /></ModuleGate>} />
                <Route path="/organisations" element={<ModuleGate module="organisations"><Organisations /></ModuleGate>} />
                <Route path="/volunteers" element={<ModuleGate module="volunteers"><Volunteers /></ModuleGate>} />
                <Route path="/assets" element={<ModuleGate module="assets"><Assets /></ModuleGate>} />
                <Route path="/reports" element={<ModuleGate module="reports"><Reports /></ModuleGate>} />
                <Route path="/finance" element={<ModuleGate module="finance"><FreeAgent /></ModuleGate>} />
                <Route path="/settings" element={<Settings />} />
                <Route
                  path="/team-management"
                  element={
                    <AdminRoute>
                      <TeamManagement />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/system-settings"
                  element={
                    <AdminRoute>
                      <SystemSettings />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/system-status"
                  element={
                    <AdminRoute>
                      <SystemStatus />
                    </AdminRoute>
                  }
                />
              </Routes>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
